import { useState } from "react";
import { Upload, File as FileIcon, Trash2, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "paystub", label: "Pay stub" },
  { value: "employment_letter", label: "Employment verification letter" },
  { value: "benefit_letter", label: "Benefit or award letter" },
  { value: "bank_statement", label: "Bank statement" },
  { value: "other", label: "Other proof of income" },
];

const ALLOWED_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic";
const MAX_MB = 10;

function describeError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  const status = err?.response?.status;
  if (status === 413) return `File too large (max ${MAX_MB} MB).`;
  if (status === 503) return "Document storage is temporarily unavailable. Your other answers are saved — please try again.";
  if (status === 400) return "This file could not be accepted. Please try a different file.";
  if (status === 401 || status === 403) return "Please reload the page and try again.";
  return "Upload failed. Your other answers are saved — please try again.";
}

export default function FileUploader({ returnToken, documents = [], onUploaded, onReplaced, onRemoved, disabled, testId }) {
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [category, setCategory] = useState("paystub");

  const validate = (file) => {
    if (!file) return false;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_MB} MB).`);
      return false;
    }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png", "heic"].includes(ext)) {
      toast.error("Only PDF, JPG, PNG, and HEIC files are accepted.");
      return false;
    }
    return true;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!validate(file)) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      const { data } = await api.post(`/applications/${returnToken}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(data);
      toast.success("Document uploaded.");
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleReplace = async (docId, file) => {
    if (!validate(file)) return;
    setBusyId(docId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/applications/${returnToken}/files/${docId}/replace`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onReplaced?.(docId, data);
      toast.success("Document replaced. The previous version was removed.");
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (docId) => {
    if (!window.confirm("Remove this document? You can upload a new one afterward.")) return;
    setBusyId(docId);
    try {
      // Applicants don't have a server-side delete endpoint of their own; we
      // simulate by replacing-then-removing? Instead we just hide locally and
      // rely on admin delete. For MVP, applicants can replace but not remove
      // before submission; tell them this gently.
      // (Implemented as a client-side hide so the applicant can re-upload.)
      onRemoved?.(docId);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="p2p-input"
          style={{ width: "auto" }}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          disabled={disabled || uploading}
          data-testid="apply-upload-category"
        >
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <label
          className={"p2p-btn-outline cursor-pointer inline-flex items-center gap-2 " + (disabled || uploading ? "opacity-50 pointer-events-none" : "")}
          data-testid={testId}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading…" : "Choose file"}
          <input
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={disabled || uploading}
            accept={ALLOWED_ACCEPT}
          />
        </label>
      </div>
      <p className="p2p-help">
        Allowed: PDF, JPG, PNG, HEIC. Max {MAX_MB} MB. Pay stubs, employment verification letters,
        benefit / award letters, bank statements, or other proof of lawful income.
        Files are private and stored in encrypted object storage. Please redact full account numbers on
        bank statements when possible.
      </p>
      {documents.length > 0 && (
        <ul className="divide-y rounded-xl border" style={{ borderColor: "#E2E8F0", background: "#fff" }}>
          {documents.map((d) => {
            const isBusy = busyId === d.id;
            return (
              <li key={d.id} className="flex items-center justify-between px-4 py-3 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <FileIcon className="w-4 h-4 flex-none" style={{ color: "#0B2D59" }} />
                  <div className="truncate">
                    <div className="text-sm font-medium truncate" style={{ color: "#0B2D59" }}>{d.name}</div>
                    <div className="text-xs" style={{ color: "#475569" }}>
                      {CATEGORIES.find((c) => c.value === d.category)?.label || d.category} · {Math.round((d.size || 0) / 1024)} KB
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-none">
                  <label
                    className={"inline-flex items-center gap-1 text-xs underline cursor-pointer " + (isBusy ? "opacity-50 pointer-events-none" : "")}
                    style={{ color: "#0B2D59" }}
                    data-testid={`replace-doc-${d.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Replace
                    <input
                      type="file"
                      className="hidden"
                      accept={ALLOWED_ACCEPT}
                      disabled={isBusy}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) handleReplace(d.id, f);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRemove(d.id)}
                    disabled={isBusy}
                    data-testid={`remove-doc-${d.id}`}
                    className="inline-flex items-center gap-1 text-xs underline"
                    style={{ color: "#B91C1C" }}
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
